import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faCode,
  faFont,
  faHighlighter,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faRedo,
  faRemoveFormat,
  faStrikethrough,
  faTable,
  faUnderline,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import { BackgroundColor, FontSize, LineHeight, TextStyle } from "@tiptap/extension-text-style";
import { TableKit } from "@tiptap/extension-table";

const EMPTY_HTML = "<p></p>";

export const isRichTextEmpty = (html) => {
  if (!html) return true;
  const text = document.createElement("div");
  text.innerHTML = html;
  return !text.textContent.replace(/\u00a0/g, " ").trim() && !text.querySelector("img, video, iframe, table");
};

function ToolbarButton({ active, children, label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className={`btn btn-sm rich-text-editor__button ${active ? "active" : ""}`}
      title={label}
      aria-label={label}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Shared rich-text editor. It intentionally emits ordinary HTML so
 * existing Shopify metafields and eBay templates continue using the same contract.
 */
export default function RichTextEditor({ content, onChange, placeholder = "Write the description here.", ariaLabel = "Rich text editor", minHeight = 120 }) {
  const [showSource, setShowSource] = useState(false);
  const [source, setSource] = useState(content || EMPTY_HTML);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
        },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      BackgroundColor,
      FontSize,
      LineHeight,
      TableKit.configure({ table: { resizable: true } }),
    ],
    content: content || EMPTY_HTML,
    editorProps: {
      attributes: {
        class: "rich-text-editor__content",
        "aria-label": ariaLabel,
        style: `min-height: ${minHeight}px`,
      },
    },
    onUpdate: ({ editor: nextEditor }) => onChange(nextEditor.getHTML()),
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) editor.commands.setContent(content || EMPTY_HTML, false);
  }, [content, editor]);

  useEffect(() => {
    if (!showSource && editor) setSource(editor.getHTML());
  }, [editor, showSource]);

  if (!editor) return <div className="rich-text-editor__loading">Opening editor…</div>;

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const href = window.prompt("Link URL", previous);
    if (href === null) return;
    const chain = editor.chain().focus().extendMarkRange("link");
    if (!href.trim()) chain.unsetLink().run();
    else chain.setLink({ href: href.trim() }).run();
  };

  const applySource = () => {
    editor.commands.setContent(source || EMPTY_HTML);
    setShowSource(false);
  };

  const setHeading = (value) => {
    const chain = editor.chain().focus();
    if (value === "paragraph") chain.setParagraph().run();
    else if (value === "blockquote") chain.toggleBlockquote().run();
    else if (value === "codeBlock") chain.toggleCodeBlock().run();
    else chain.toggleHeading({ level: Number(value) }).run();
  };

  const currentBlock = editor.isActive("blockquote")
    ? "blockquote"
    : editor.isActive("codeBlock")
      ? "codeBlock"
      : editor.isActive("heading", { level: 1 }) ? "1"
        : editor.isActive("heading", { level: 2 }) ? "2"
          : editor.isActive("heading", { level: 3 }) ? "3"
            : editor.isActive("heading", { level: 4 }) ? "4"
              : editor.isActive("heading", { level: 5 }) ? "5"
                : editor.isActive("heading", { level: 6 }) ? "6" : "paragraph";

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label={`${ariaLabel} formatting`}>
        <div className="rich-text-editor__group">
          <ToolbarButton label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><FontAwesomeIcon icon={faUndo} /></ToolbarButton>
          <ToolbarButton label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><FontAwesomeIcon icon={faRedo} /></ToolbarButton>
        </div>
        <div className="rich-text-editor__group">
          <select className="custom-select custom-select-sm rich-text-editor__select" value={currentBlock} aria-label="Block style" onChange={(event) => setHeading(event.target.value)}>
            <option value="paragraph">Paragraph</option>
            <option value="1">Heading 1</option><option value="2">Heading 2</option><option value="3">Heading 3</option>
            <option value="4">Heading 4</option><option value="5">Heading 5</option><option value="6">Heading 6</option>
            <option value="blockquote">Quote</option><option value="codeBlock">Code block</option>
          </select>
          <ToolbarButton label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><FontAwesomeIcon icon={faBold} /></ToolbarButton>
          <ToolbarButton label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><FontAwesomeIcon icon={faItalic} /></ToolbarButton>
          <ToolbarButton label="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><FontAwesomeIcon icon={faUnderline} /></ToolbarButton>
          <ToolbarButton label="Strike through" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><FontAwesomeIcon icon={faStrikethrough} /></ToolbarButton>
          <ToolbarButton label="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><FontAwesomeIcon icon={faRemoveFormat} /></ToolbarButton>
        </div>
        <div className="rich-text-editor__group">
          <ToolbarButton label="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><FontAwesomeIcon icon={faListUl} /></ToolbarButton>
          <ToolbarButton label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FontAwesomeIcon icon={faListOl} /></ToolbarButton>
          <select className="custom-select custom-select-sm rich-text-editor__select rich-text-editor__select--align" value={editor.getAttributes("paragraph").textAlign || editor.getAttributes("heading").textAlign || "left"} aria-label="Text alignment" onChange={(event) => editor.chain().focus().setTextAlign(event.target.value).run()}>
            <option value="left">Align left</option><option value="center">Align center</option><option value="right">Align right</option><option value="justify">Justify</option>
          </select>
          <ToolbarButton label="Add or edit link" active={editor.isActive("link")} onClick={setLink}><FontAwesomeIcon icon={faLink} /></ToolbarButton>
        </div>
        <div className="rich-text-editor__group">
          <label className="rich-text-editor__color" title="Text color"><FontAwesomeIcon icon={faFont} /><span className="rich-text-editor__color-swatch" style={{ backgroundColor: editor.getAttributes("textStyle").color || "#000000" }} /><input type="color" aria-label="Text color" value={editor.getAttributes("textStyle").color || "#000000"} onChange={(event) => editor.chain().focus().setColor(event.target.value).run()} /></label>
          <label className="rich-text-editor__color" title="Text highlight color"><FontAwesomeIcon icon={faHighlighter} /><span className="rich-text-editor__color-swatch" style={{ backgroundColor: editor.getAttributes("textStyle").backgroundColor || "#ffffff" }} /><input type="color" aria-label="Text highlight color" value={editor.getAttributes("textStyle").backgroundColor || "#ffffff"} onChange={(event) => editor.chain().focus().setBackgroundColor(event.target.value).run()} /></label>
          <select className="custom-select custom-select-sm rich-text-editor__select rich-text-editor__select--size" value={editor.getAttributes("textStyle").fontSize || ""} aria-label="Font size" onChange={(event) => event.target.value ? editor.chain().focus().setFontSize(event.target.value).run() : editor.chain().focus().unsetFontSize().run()}>
            <option value="">Font size</option><option value="12px">12</option><option value="14px">14</option><option value="16px">16</option><option value="18px">18</option><option value="24px">24</option><option value="32px">32</option>
          </select>
          <select className="custom-select custom-select-sm rich-text-editor__select rich-text-editor__select--height" value={editor.getAttributes("textStyle").lineHeight || ""} aria-label="Line height" onChange={(event) => event.target.value ? editor.chain().focus().setLineHeight(event.target.value).run() : editor.chain().focus().unsetLineHeight().run()}>
            <option value="">Line height</option><option value="1">1</option><option value="1.25">1.25</option><option value="1.5">1.5</option><option value="2">2</option>
          </select>
        </div>
        <div className="rich-text-editor__group">
          <ToolbarButton label="Insert table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><FontAwesomeIcon icon={faTable} /></ToolbarButton>
          {editor.isActive("table") && <>
            <ToolbarButton label="Add table row" onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row</ToolbarButton>
            <ToolbarButton label="Add table column" onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col</ToolbarButton>
            <ToolbarButton label="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>− Table</ToolbarButton>
          </>}
          <ToolbarButton label="Edit HTML source" active={showSource} onClick={() => { setSource(editor.getHTML()); setShowSource(!showSource); }}><FontAwesomeIcon icon={faCode} /></ToolbarButton>
        </div>
      </div>
      {showSource ? <div className="rich-text-editor__source"><textarea className="form-control" value={source} onChange={(event) => setSource(event.target.value)} aria-label={`${ariaLabel} HTML source`} /><div><button type="button" className="btn btn-sm btn-primary mr-2" onClick={applySource}>Apply HTML</button><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowSource(false)}>Cancel</button></div></div> : <EditorContent editor={editor} />}
    </div>
  );
}
